'use client';

import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

// Define the shape of our loading state
type LoadingState = Record<string, boolean>;

// Create a context for our loading state
type LoadingContextType = {
  loadingState: LoadingState;
  registerComponent: (key: string[]) => void;
  setLoaded: (key?: string) => void;
  isAllLoaded: boolean;
  setTotal: (total: number) => void;
};

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

// Create a provider component
function LoadingProvider({ children }: React.PropsWithChildren) {
  const [loadingState, setLoadingState] = useState<LoadingState>({});
  const [total, setTotal] = useState<number>(0);
  const isFetching = useIsFetching();
  const registerComponent = useCallback((key: string[]) => {
    key.forEach(k => {
      setLoadingState(prev => ({ ...prev, [k]: false }));
    });
  }, []);

  const setLoaded = useCallback((key?: string) => {
    if (key) {
      setLoadingState(prev => ({ ...prev, [key]: true }));
    }
  }, []);

  const isAllLoaded = Object.values(loadingState).length
    ? Object.values(loadingState).every(state => state) && isFetching === 0
    : false;
  return (
    <LoadingContext.Provider
      value={useMemo(
        () => ({
          loadingState,
          registerComponent,
          setTotal,
          setLoaded,
          isAllLoaded,
        }),
        [loadingState, registerComponent, setTotal, setLoaded, isAllLoaded]
      )}
    >
      {children}
    </LoadingContext.Provider>
  );
}

// Create a custom hook to use the loading context
function useLoading(componentKey?: string[]) {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }

  const { loadingState, registerComponent, setLoaded, setTotal, isAllLoaded } =
    context;

  useEffect(() => {
    if (componentKey?.length) {
      registerComponent(componentKey);
    }
  }, []);

  const isLoaded = (key: string) => loadingState[key];

  return {
    isLoaded,
    setLoaded: (key: string) => setLoaded(key),
    isAllLoaded,
    setTotal,
  };
}

export { LoadingProvider, useLoading };
